import { api } from '@/config/api';

export interface FbPageItem {
  id: string;
  message?: string;
  created_time?: string;
  permalink_url?: string;
  full_picture?: string;
  /**
   * Permanent ImageKit-hosted mirror of `full_picture`. Prefer this when
   * rendering — `full_picture` is a signed FB CDN URL that expires in a
   * few hours. Backend mirrors on first sync.
   */
  imagekit_url?: string;
  status_type?: string;
  attachments?: { data: Array<{ media_type?: string; type?: string; title?: string; description?: string }> };
  reactions?: { summary?: { total_count?: number } };
  comments?: { summary?: { total_count?: number } };
  shares?: { count?: number };
}

export interface FbPageListResponse {
  data: FbPageItem[];
  paging?: { cursors?: { before?: string; after?: string }; next?: string };
  pageId: string;
  pageName?: string;
  fetchedAt: string;
  fromCache: boolean;
}

export interface FbPagePostInsightsResponse {
  postId: string;
  metrics: Array<{ name: string; value: number; title?: string; description?: string }>;
  fetchedAt: string;
}

const BASE = '/slighter_crm/facebook/page';


export type FbPagePostType = 'TEXT' | 'LINK' | 'IMAGE' | 'MULTI_IMAGE' | 'VIDEO' | 'STORY';

export interface PublishPagePostPayload {
  post_type: FbPagePostType;
  message?: string;
  link?: string;
  image_url?: string;
  image_urls?: string[];
  video_url?: string;
  video_title?: string;
  /** Unix seconds. Facebook requires 10 min – 6 months ahead; ignored for STORY. */
  scheduled_publish_time?: number;
  page_id?: string;
}

export interface PublishPagePostResult {
  postId: string;
  pageId: string;
  permalink?: string;
  scheduled: boolean;
  publishedAt: string;
}

export const facebookPageService = {
  posts: async (
    refresh = false,
    limit?: number,
    pageId?: string,
  ): Promise<FbPageListResponse> =>
    (await api.get(`${BASE}/posts`, {
      params: { refresh: refresh ? '1' : undefined, limit, pageId },
    })).data,

  postInsights: async (postId: string): Promise<FbPagePostInsightsResponse> =>
    (await api.get(`${BASE}/posts/${postId}/insights`)).data,

  publish: async (payload: PublishPagePostPayload): Promise<PublishPagePostResult> =>
    (await api.post('/slighter_crm/facebook/page/publish', payload, { timeout: 180_000 })).data,
  deletePost: async (postId: string): Promise<{ ok: true }> =>
    (await api.delete(`${BASE}/posts/${postId}`)).data,
};

/** Sum all `post_reactions_<type>_total` from an insights response. */
export function sumReactions(metrics: FbPagePostInsightsResponse['metrics']): number {
  let total = 0;
  for (const m of metrics) {
    if (m.name.startsWith('post_reactions_') && m.name.endsWith('_total')) {
      total += m.value;
    }
  }
  return total;
}
